// src/mocks/handlers/funding/advancesDetailedHandler.js

import { rest } from 'msw';
import invoicesDetailed from '@/mocks/data/fundingInvoicesDetailedData.json';
import { API_ENDPOINTS } from '@/services/api/endpoints';
import { VITE_MSW_BASE_URL } from '@/config';
import {
  IInvoicesDetailed,
  TInvoicesDetailedSortBy,
} from '@/domains/funding/invoices.types';
import { API_DEFAULT_PAGE_SIZE } from '@/services/api/api.constants';
import { matchesDateRange } from '@/mocks/lib/matchesDateRange';
import { matchesAgencyFilter } from '@/mocks/lib/matchesAgencyFilter';
import { PaginatedResponse } from '@/shared/types/api';
import { isAccessTokenExpired } from '@/mocks/lib/authTokenUtils';

/** MSW-only: links detailed lines to summary rows for drilldown filtering. */
type InvoicesDetailedMockRow = IInvoicesDetailed & {
  monthly_invoice_uuid?: string;
};

export const fundingInvoicesDetailedHandler = rest.get<
  PaginatedResponse<IInvoicesDetailed>
>(
  `${VITE_MSW_BASE_URL}${API_ENDPOINTS.FUNDING_INVOICES_DETAILED}`,
  (req, res, ctx) => {
    const token = req.headers.get('Authorization');
    if (!token) {
      return res(ctx.status(500), ctx.json({ detail: 'Something went wrong' }));
    }

    if (isAccessTokenExpired()) {
      return res(ctx.status(401), ctx.json({ error: 'Access token expired' }));
    }

    const { customer_uuid } = req.params as { customer_uuid: string };
    const group = invoicesDetailed.find((g) => g.uuid === customer_uuid);
    if (!group) {
      return res(
        ctx.status(200),
        ctx.json({ total: 0, page: 1, pages: 1, results: [] }),
      );
    }

    const url = new URL(req.url);
    const params = url.searchParams;

    const status = params.get('statuses');
    const currencies = params.getAll('currencies');
    const networks = params.getAll('networks');
    const sources = params.getAll('sources');
    const agencies = params.getAll('agencies');
    const campaigns = params.getAll('campaigns');
    const monthly_invoice_uuid = params.get('monthly_invoice_uuid');
    const event_date_start = params.get('event_date_start');
    const event_date_end = params.get('event_date_end');
    const expected_repayment_date_start = params.get(
      'expected_repayment_date_start',
    );
    const expected_repayment_date_end = params.get(
      'expected_repayment_date_end',
    );
    const ordering = (params.get('ordering') as TInvoicesDetailedSortBy) || '';
    const page = parseInt(params.get('page') || '1', 10);
    const page_size = parseInt(
      params.get('page_size') || `${API_DEFAULT_PAGE_SIZE}`,
      10,
    );
    const search = params.get('search') || '';

    const detailedRows = (group.results as InvoicesDetailedMockRow[]).map(
      (item) => {
        if (typeof item.year === 'number' && typeof item.period === 'number') {
          return item;
        }

        const advanceDate = new Date(item.advance_date);
        const fallbackYear = Number.isNaN(advanceDate.getTime())
          ? new Date().getFullYear()
          : advanceDate.getUTCFullYear();
        const fallbackPeriod = Number.isNaN(advanceDate.getTime())
          ? 1
          : advanceDate.getUTCMonth() + 1;

        return {
          ...item,
          year: item.year ?? fallbackYear,
          period: item.period ?? fallbackPeriod,
        };
      },
    );

    let filtered = detailedRows.filter((item) => {
      const matchStatus = status ? item.status === status : true;
      const matchCurrency =
        currencies.length > 0 ? currencies.includes(item.currency) : true;
      const matchNetwork =
        networks.length > 0 && item.network_name
          ? networks.includes(item.network_name)
          : true;
      const matchSource =
        sources.length > 0 && item.source_name
          ? sources.includes(item.source_name)
          : true;
      const matchAgency = matchesAgencyFilter(item.agency_name, agencies);
      const matchCampaign =
        campaigns.length > 0
          ? item.campaign_uuid
            ? campaigns.includes(item.campaign_uuid)
            : false
          : true;
      const matchMonthlyInvoice = monthly_invoice_uuid
        ? item.monthly_invoice_uuid === monthly_invoice_uuid
        : true; // mock row field; not returned by real API
      const matchEventDate =
        event_date_start && event_date_end
          ? matchesDateRange(
              item.advance_date,
              event_date_start,
              event_date_end,
            )
          : true;
      const matchExpectedRepaymentDate =
        expected_repayment_date_start && expected_repayment_date_end
          ? matchesDateRange(
              item.expected_repayment_date,
              expected_repayment_date_start,
              expected_repayment_date_end,
            )
          : true;

      return (
        matchStatus &&
        matchCurrency &&
        matchNetwork &&
        matchSource &&
        matchAgency &&
        matchCampaign &&
        matchMonthlyInvoice &&
        matchEventDate &&
        matchExpectedRepaymentDate
      );
    });

    if (search) {
      const lower = search.toLowerCase();
      filtered = filtered.filter((item) => {
        const haystack = [
          item.advance_id,
          item.payment_ref,
          item.source_name,
          item.network_name,
          item.campaign_name,
          item.campaign_id,
          item.status,
          item.currency,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return haystack.includes(lower);
      });
    }

    // 2. Sort
    if (ordering) {
      const isDesc = ordering.startsWith('-');
      const key = (
        isDesc ? ordering.slice(1) : ordering
      ) as TInvoicesDetailedSortBy;

      filtered = filtered.sort((a, b) => {
        if (key === 'billing_period') {
          const aBillingPeriod = a.year * 100 + a.period;
          const bBillingPeriod = b.year * 100 + b.period;

          return isDesc
            ? bBillingPeriod - aBillingPeriod
            : aBillingPeriod - bBillingPeriod;
        }

        if (a === null || a === undefined) return 1;
        if (b === null || b === undefined) return -1;
        const aValue = a[key];
        const bValue = b[key];

        if (aValue === null || aValue === undefined) return 1;
        if (bValue === null || bValue === undefined) return -1;

        if (typeof aValue === 'string' && typeof bValue === 'string') {
          return isDesc
            ? bValue.localeCompare(aValue)
            : aValue.localeCompare(bValue);
        }

        if (typeof aValue === 'number' && typeof bValue === 'number') {
          return isDesc ? bValue - aValue : aValue - bValue;
        }

        return 0;
      });
    }

    // 3. Paginate
    const total = filtered.length;
    const pages = Math.ceil(total / page_size);
    const start = Math.max(0, (page - 1) * page_size);
    const end = Math.min(start + page_size, total);
    const results: IInvoicesDetailed[] = filtered
      .slice(start, end)
      .map(({ monthly_invoice_uuid: _mockUuid, ...row }) => row);

    // 4. Response
    return res(
      ctx.status(200),
      ctx.json({
        total,
        page,
        pages,
        results,
      }),
    );
  },
);
